import SelectedCustomer from '@/platform/common/components/SelectedCustomer/SelectedCustomer';
import { localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    Header,
    Segment,
    SegmentContent,
    SegmentHeader,
} from '@/platform/ui';
import {
    CalendarIcon,
    CheckIcon,
    CurrencyEuroIcon,
    PencilIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';
import { useMemo, useState } from 'react';
import InvoiceInfoTable from './components/InvoiceInfoTable/InvoiceInfoTable';
import PayedModal from './components/PayedModal/PayedModal';

const ShowView = () => {
    const { auth, invoice } = usePage().props;
    const [modalOpened, setModalOpened] = useState(false);
    const confirm = useConfirmationStore(state => state.confirm);

    const invoiceEmail = useMemo(
        () =>
            invoice.customer?.reseller?.force_invoice === 1
                ? invoice.customer?.reseller?.invoice_email
                : invoice.customer?.invoice_email,
        [invoice]
    );

    const sendInvoice = () => {
        router.post(
            `/customers/${invoice.customer_id}/invoices/${invoice.id}/send`
        );
    };

    return (
        <Container fluid>
            <SelectedCustomer />

            <div className='grid grid-cols-1 items-start gap-4 lg:grid-cols-5'>
                <Segment className='order-2 not-first:mt-0 lg:order-1 lg:col-span-4'>
                    <SegmentHeader
                        header={`${invoice.is_credit ? 'Credit' : 'Factuur'} ${invoice.number}`}
                        subheader={
                            <>
                                Klant:{' '}
                                <a
                                    className='text-primary-500'
                                    href={`/customers/${invoice.customer_id}/info`}
                                >
                                    {invoice.customer.name}
                                </a>
                            </>
                        }
                    >
                        {auth.permissions?.includes('update invoices') &&
                            !invoice.payed_at && (
                                <>
                                    <Button
                                        onClick={() => setModalOpened(true)}
                                        type='button'
                                    >
                                        <CheckIcon className='size-4 text-green-500' />
                                        Markeren als betaald
                                    </Button>
                                    <Button
                                        variant='primary'
                                        as={Link}
                                        href={`/customers/${invoice.customer_id}/invoices/${invoice.id}/edit`}
                                    >
                                        <PencilIcon className='size-4' />
                                        Gegevens bewerken
                                    </Button>
                                </>
                            )}
                    </SegmentHeader>
                    <SegmentContent>
                        <InvoiceInfoTable />
                    </SegmentContent>
                </Segment>

                <Segment className='order-1 not-first:mt-0 lg:order-2'>
                    <SegmentContent className='flex flex-col gap-y-4'>
                        {invoice.mailed_at && (
                            <Header
                                as='h4'
                                icon={CalendarIcon}
                                subheader='Verstuurd op'
                            >
                                {localDate(invoice.mailed_at)}
                            </Header>
                        )}
                        {invoice.payed_at && (
                            <Header
                                as='h4'
                                icon={CurrencyEuroIcon}
                                subheader='Betaald op'
                            >
                                {localDate(invoice.payed_at)}
                            </Header>
                        )}
                        {auth.permissions.includes('send invoices') &&
                            !invoice.payed_at && (
                                <Button
                                    fluid
                                    variant='primary'
                                    onClick={() =>
                                        confirm({
                                            description: (
                                                <>
                                                    <p>
                                                        Weet u zeker dat deze
                                                        factuur wilt versturen
                                                        naar{' '}
                                                        <b>
                                                            {
                                                                invoice.customer
                                                                    .name
                                                            }{' '}
                                                            ({invoiceEmail})
                                                        </b>
                                                        ? U kunt daarna de
                                                        factuur niet meer
                                                        wijzigen
                                                    </p>
                                                </>
                                            ),
                                            onConfirm: close => {
                                                sendInvoice();
                                                close();
                                            },
                                        })
                                    }
                                >
                                    Versturen
                                </Button>
                            )}
                        {auth.permissions.includes('download invoices') && (
                            <Button
                                fluid
                                as={Link}
                                href={`/customers/${invoice.customer_id}/invoices/${invoice.id}/download`}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                Downloaden
                            </Button>
                        )}
                    </SegmentContent>
                </Segment>
            </div>

            <PayedModal
                open={modalOpened}
                onClose={() => setModalOpened(false)}
            />
        </Container>
    );
};

export default ShowView;
